import { ASANA_HOST } from "@/domain/ticketing"
import { isRecord } from "@/helpers/utils"

import type { AsanaWorkspace } from "@/api/useIntegrations.types"
import type { AsanaTokenFormValues } from "@/pages/IntegrationsPage/components/pages/AsanaIntegrationPage/AsanaIntegrationPage.types"
import type { AzureBoardsFormValues } from "@/pages/IntegrationsPage/components/pages/AzureBoardsIntegrationPage/AzureBoardsIntegrationPage.types"

// Mirrors the backend's check on Azure DevOps organization names
const AZURE_ORGANIZATION = /^[A-Za-z0-9][A-Za-z0-9-]{0,48}[A-Za-z0-9]$/

export const isValidAzureOrganization = (value: string) =>
  AZURE_ORGANIZATION.test(value.trim())

export const toAzureBoardsInstallPayload = (values: AzureBoardsFormValues) => ({
  azureBoards: {
    organization: values.organization.trim(),
    pat: values.pat.trim()
  }
})

export const toAsanaInstallPayload = (values: AsanaTokenFormValues) => ({
  asana: { token: values.token.trim(), workspaceGid: values.workspaceGid }
})

export const AZURE_BOARDS_FIELDS: (keyof AzureBoardsFormValues)[] = [
  "organization",
  "pat"
]

export const ASANA_TOKEN_FIELDS: (keyof AsanaTokenFormValues)[] = [
  "token",
  "workspaceGid"
]

export const toFieldErrors = (
  names: string[],
  fields: Record<string, string>
) =>
  names
    .filter((name) => fields[name])
    .map((name) => ({ name, errors: [fields[name]] }))

const isAsanaWorkspace = (value: unknown): value is AsanaWorkspace =>
  isRecord(value) &&
  typeof value.gid === "string" &&
  typeof value.name === "string"

export const getAsanaWorkspaces = (error: unknown) => {
  const data = isRecord(error) ? error.data : undefined
  const workspaces = isRecord(data) ? data.workspaces : undefined

  return Array.isArray(workspaces) ? workspaces.filter(isAsanaWorkspace) : []
}

// The browser only ever leaves for Asana's own consent page
export const isAsanaAuthorizeUrl = (value?: string) => {
  try {
    const url = new URL(value || "")

    return url.protocol === "https:" && url.hostname === ASANA_HOST
  } catch {
    return false
  }
}

// OAuth codes and states are single-use secrets; keep them out of logs
const SECRET_CALLBACK_PARAMS = ["code", "state"]

export const toLoggedCallbackParams = (params: Record<string, string>) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([key]) => !SECRET_CALLBACK_PARAMS.includes(key)
    )
  )
